import axios from 'axios'

import { resolveApiBaseUrl, resolveApiResourceUrl } from '@/services/apiUrlService'
import { generateId } from '@/services/idService'
import { throwIfOfflineReadOnly } from '@/services/offlineModificationGuardService'
import type { SmartSearchAppResult } from '@/services/smartSearchService'
import { showError } from '@/services/toastService'
import { useAuthStore } from '@/stores/authStore'
import type {
  AccountInvitation,
  AccountInvitationCreateResponse,
  InvitationQuotaStatus,
  MessageResponse,
} from '@/types/accountInvitation'
import { EventType } from '@/types/event'
import type { Event } from '@/types/event'
import type { FavoritePlace, FavoritePlaceCreateRequest } from '@/types/favoritePlace'
import type { WorkspacePOI } from '@/types/poi'
import type { Tour, ToursListResponse } from '@/types/tour'
import type { InvitedUser } from '@/types/user'
import type { WorkspaceMember, WorkspaceInvitation } from '@/types/workspace'

// API base URL from environment
const API_BASE_URL = resolveApiBaseUrl() || '/api/v1'

// Tauri apps run on tauri://localhost — all API requests are cross-origin and
// WKWebView will not store or send cookies (SameSite + ITP restrictions).
// Instead, the sessionToken from the login response is sent as X-Session-Token header.
export const IS_TAURI = typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window

const CSRF_HEADER_NAME = 'X-CSRFToken'
const CSRF_SAFE_METHODS = new Set(['get', 'head', 'options', 'trace'])
const CSRF_COOKIE_CANDIDATES = ['csrftoken', 'routecamp_csrftoken']

function readCookie(name: string): string | null {
  if (typeof document === 'undefined' || !document.cookie) {
    return null
  }

  const cookiePrefix = `${name}=`
  const cookies = document.cookie.split(';')

  for (const rawCookie of cookies) {
    const cookie = rawCookie.trim()
    if (cookie.startsWith(cookiePrefix)) {
      return decodeURIComponent(cookie.slice(cookiePrefix.length))
    }
  }

  return null
}

function resolveCsrfToken(): string | null {
  for (const name of CSRF_COOKIE_CANDIDATES) {
    const token = readCookie(name)
    if (token) {
      return token
    }
  }
  return null
}

// Add workspace search response types
// Basic track interface for API responses
interface APITrack {
  clientId?: string
  name?: string
  pointsLineReduced500m?: string
  startTime?: number // Unix timestamp (milliseconds) of track start
  endTime?: number // Unix timestamp (milliseconds) of track end
}

export interface WorkspaceSearchItem {
  clientId: string
  name: string
  description?: string | null
  isPublic: boolean
  isOwner: boolean
  userRole?: string | null
  canEdit?: boolean | null
  canManage?: boolean | null
  canView?: boolean | null
  isBookmarked?: boolean
  ownerNames?: string[] | null
  poiCount?: number
  photoCount?: number
  coverPhotoThumbnailUrl?: string | null
  type: number
  createdAt?: string | null
  lastModifiedAt?: string | null
  activityStart?: string | null
  activityEnd?: string | null
  tours: Array<{
    clientId: string
    name: string
    isPinned?: boolean
    tracks: APITrack[]
  }>
  isPinned?: boolean
}

export interface WorkspaceCatalogResponse {
  workspaces: WorkspaceSearchItem[]
  personalTourCount: number
}

export interface UiSettingPayload {
  key: string
  value: unknown
}

export interface UiSettingResponse extends UiSettingPayload {
  updatedAt: string
}

export interface UiSettingsResponse {
  settings: UiSettingResponse[]
}

export interface MaintenanceStatusResponse {
  active: boolean
}

interface RawUiSettingResponse {
  key: string
  value: unknown
  updated_at: string
}

interface RawUiSettingsResponse {
  settings: RawUiSettingResponse[]
}

function normalizeUiSettingsResponse(data: RawUiSettingsResponse): UiSettingsResponse {
  return {
    settings: data.settings.map((setting) => ({
      key: setting.key,
      value: setting.value,
      updatedAt: setting.updated_at,
    })),
  }
}

/**
 * Convert camelCase to snake_case
 */
function camelToSnakeCase(str: string): string {
  if (!str) {
    return ''
  }
  // Handle acronyms (e.g., XMLHTTPRequest -> XML_HTTPRequest)
  // Handle transitions from lowercase/digit to uppercase (e.g., myVariable -> my_Variable, version2Update -> version2_Update)
  // Handle transitions from letter to digit (e.g., Reduced500m -> Reduced_500m, item1 -> item_1)
  // Convert to lowercase
  return str
    .replace(/([A-Z]+)([A-Z][a-z0-9])/g, '$1_$2')
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/([A-Za-z])([0-9])/g, '$1_$2')
    .toLowerCase()
}

/**
 * Convert an object with camelCase keys to snake_case keys recursively
 * Also converts field values to snake_case when the key is 'field'
 */
export function convertToSnakeCase<T>(obj: T): T {
  if (obj === null || obj === undefined) {
    return obj
  }

  if (Array.isArray(obj)) {
    return obj.map(convertToSnakeCase) as T
  }

  if (typeof obj === 'object' && obj.constructor === Object) {
    const converted: Record<string, unknown> = {}

    for (const [key, value] of Object.entries(obj)) {
      const snakeKey = camelToSnakeCase(key)

      // Special handling for 'field' property values - convert them to snake_case too
      if (key === 'field' && typeof value === 'string') {
        converted[snakeKey] = camelToSnakeCase(value)
      } else {
        converted[snakeKey] = convertToSnakeCase(value)
      }
    }

    return converted as T
  }

  return obj
}

/**
 * Convert snake_case to camelCase
 */
function snakeToCamelCase(str: string): string {
  return str.replace(/_([a-z0-9])/g, (_, char) => {
    // Check if char is a letter (a-z)
    if (char >= 'a' && char <= 'z') {
      return char.toUpperCase()
    }
    // If it's a number (0-9), just return the number itself
    return char
  })
}

/**
 * Convert an object with snake_case keys to camelCase keys recursively
 */
export function convertToCamelCase<T>(obj: T): T {
  if (obj === null || obj === undefined) {
    return obj
  }

  if (Array.isArray(obj)) {
    return obj.map(convertToCamelCase) as T
  }

  if (typeof obj === 'object' && obj.constructor === Object) {
    const converted: Record<string, unknown> = {}

    for (const [key, value] of Object.entries(obj)) {
      const camelKey = snakeToCamelCase(key)
      converted[camelKey] = convertToCamelCase(value)
    }

    return converted as T
  }

  return obj
}

// Response interfaces
interface WorkspaceDetailResponse {
  clientId: string
  name: string
  description?: string | null
  isPublic: boolean
  systemKey?: string | null
  isSystem?: boolean
  userRole?: string | null
  canEdit?: boolean | null
  canManage?: boolean | null
  canView?: boolean | null
  isOwner?: boolean
  isBookmarked?: boolean
  ownerNames?: string[] | null
  markedForDeletion?: boolean
  deletionScheduledAt?: string | null
  coverPhotoClientId?: string | null
  coverPhotoThumbnailUrl?: string | null
  coverPhotoUrl?: string | null
  tours: Tour[]
  pois?: WorkspacePOI[]
  photos: {
    clientId: string
    name?: string
    hasCoordinates: boolean
    coordinateSource?: 'exif' | 'interpolated' | 'none'
    lat: number | null
    lon: number | null
    takenAt: number | null
    url: string | null
    thumbnailUrl: string | null
    canEdit?: boolean | null
    uploadedByName?: string | null
  }[]
}

export interface PhotoAssetUrlResponse {
  clientId: string
  url: string | null
  thumbnailUrl: string | null
}

interface WorkspacePhotoAssetUrlsResponse {
  photos: PhotoAssetUrlResponse[]
}

interface WorkspaceBookmarkResponse {
  success: boolean
  isBookmarked: boolean
}

/** Structured per-event rejection from /events/batch (backend ask #1, sync-feedback.md §12). */
export interface FailedEvent {
  clientId: string
  code: string
  message: string
}

interface BatchResponse {
  message: string
  count: number
  failedEvents?: FailedEvent[]
}

interface SmartSearchResponse {
  results: SmartSearchAppResult[]
}

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: !IS_TAURI,
})

// Add request interceptor to include CSRF token
api.interceptors.request.use(
  (config) => {
    const updatedConfig = { ...config }
    updatedConfig.headers = updatedConfig.headers ?? {}

    const headers = updatedConfig.headers as
      | Record<string, unknown>
      | { set?: (key: string, value: string) => void }

    const setHeader = (key: string, value: string) => {
      if (typeof (headers as { set?: (k: string, v: string) => void }).set === 'function') {
        ;(headers as { set: (k: string, v: string) => void }).set(key, value)
      } else {
        ;(headers as Record<string, unknown>)[key] = value
      }
    }

    if (IS_TAURI) {
      const authStore = useAuthStore()
      const sessionToken = authStore.user?.sessionToken
      if (sessionToken) {
        setHeader('X-Session-Token', sessionToken)
      }
    } else {
      const method = (updatedConfig.method || 'get').toLowerCase()
      if (!CSRF_SAFE_METHODS.has(method)) {
        const csrfToken = resolveCsrfToken()
        if (csrfToken) {
          setHeader(CSRF_HEADER_NAME, csrfToken)
        }
      }
    }

    return updatedConfig
  },
  (error) => {
    return Promise.reject(error)
  },
)

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      const authStore = useAuthStore()
      if (authStore.isAuthenticated) {
        void authStore.forceLogout()
      }
    }
    return Promise.reject(error)
  },
)

class APIService {
  private getAbsoluteUrl(endpoint: string): string {
    const uri = api.getUri({ url: endpoint })
    return resolveApiResourceUrl(uri) ?? uri
  }

  private buildTourEndpoint(tourId: string, subPath?: string): string {
    const suffix = subPath ? `/${subPath.replace(/^\/+/, '')}` : ''
    return `/tours/${tourId}${suffix}`
  }

  private buildTrackEndpoint(trackId: string, subPath?: string): string {
    const encodedTrackId = encodeURIComponent(trackId)
    const suffix = subPath ? `/${subPath.replace(/^\/+/, '')}` : ''
    return `/tours/tracks/${encodedTrackId}${suffix}`
  }

  getTourSourceFileUrl(tourId: string): string {
    return this.getAbsoluteUrl(this.buildTourEndpoint(tourId, 'source-file'))
  }

  // Options for API requests to control user toasts
  private maybeToastError(
    error: unknown,
    fallbackMessage: string,
    options?: { suppressToast?: boolean; toastMessage?: string },
  ) {
    if (options?.suppressToast) return
    // Skip auth errors (handled elsewhere)
    if (axios.isAxiosError(error) && error.response) {
      const status = error.response.status
      if (status === 401 || status === 403) return
    }
    const message = error instanceof Error ? error.message : String(error)
    const prefix = options?.toastMessage || fallbackMessage
    showError(`${prefix}: ${message}`)
  }

  /**
   * Generic method for making authenticated POST requests
   * Used by auth service and other services
   */
  async post<T>(
    endpoint: string,
    data?: unknown,
    headers?: Record<string, string>,
    options?: { suppressToast?: boolean; toastMessage?: string },
  ): Promise<T & { success: boolean }> {
    throwIfOfflineReadOnly(`api:post:${endpoint}`)

    try {
      const response = await api.post<T>(endpoint, convertToSnakeCase(data), { headers })
      const convertedData = convertToCamelCase(response.data)
      return {
        ...convertedData,
        success: response.status >= 200 && response.status < 300,
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        // For authentication errors (401/403), preserve the original axios error
        // so the sync service can detect the status code
        if (error.response.status === 401 || error.response.status === 403) {
          throw error
        }

        // For other errors, create a descriptive error message
        const errorData = error.response.data as { message?: string }
        const errorMessage = errorData.message || 'Request failed'
        const wrapped = new Error(errorMessage)
        this.maybeToastError(wrapped, 'Request failed', options)
        throw wrapped
      }
      const wrapped = new Error('Request failed')
      this.maybeToastError(error, 'Request failed', options)
      throw wrapped
    }
  }

  /**
   * Generic method for making authenticated GET requests
   */
  async get<T>(
    endpoint: string,
    headers?: Record<string, string>,
    options?: { suppressToast?: boolean; toastMessage?: string },
  ): Promise<T & { success: boolean }> {
    try {
      const response = await api.get<T>(endpoint, { headers })
      const convertedData = convertToCamelCase(response.data)
      return {
        ...convertedData,
        success: response.status >= 200 && response.status < 300,
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        // For authentication errors (401/403), preserve the original axios error
        // so the sync service can detect the status code
        if (error.response.status === 401 || error.response.status === 403) {
          throw error
        }

        // For other errors, create a descriptive error message
        const errorData = error.response.data as { message?: string }
        const errorMessage = errorData.message || 'Request failed'
        const wrapped = new Error(errorMessage)
        this.maybeToastError(wrapped, 'Request failed', options)
        throw wrapped
      }
      const wrapped = new Error('Request failed')
      this.maybeToastError(error, 'Request failed', options)
      throw wrapped
    }
  }

  /**
   * Generic method for making authenticated GET requests that return a plain list.
   */
  async getList<T>(
    endpoint: string,
    headers?: Record<string, string>,
    options?: { suppressToast?: boolean; toastMessage?: string },
  ): Promise<T[]> {
    try {
      const response = await api.get<T[]>(endpoint, { headers })
      return convertToCamelCase(response.data) as T[]
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        if (error.response.status === 401 || error.response.status === 403) {
          throw error
        }

        const errorData = error.response.data as { message?: string }
        const errorMessage = errorData.message || 'Request failed'
        const wrapped = new Error(errorMessage)
        this.maybeToastError(wrapped, 'Request failed', options)
        throw wrapped
      }
      const wrapped = new Error('Request failed')
      this.maybeToastError(error, 'Request failed', options)
      throw wrapped
    }
  }

  /**
   * Generic method for making authenticated DELETE requests
   */
  async delete<T>(
    endpoint: string,
    headers?: Record<string, string>,
    options?: { suppressToast?: boolean; toastMessage?: string },
  ): Promise<T & { success: boolean }> {
    throwIfOfflineReadOnly(`api:delete:${endpoint}`)

    try {
      const response = await api.delete<T>(endpoint, { headers })
      const convertedData = convertToCamelCase(response.data)
      return {
        ...convertedData,
        success: response.status >= 200 && response.status < 300,
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        // For authentication errors (401/403), preserve the original axios error
        // so the sync service can detect the status code
        if (error.response.status === 401 || error.response.status === 403) {
          throw error
        }

        // For other errors, create a descriptive error message
        const errorData = error.response.data as { message?: string }
        const errorMessage = errorData.message || 'Request failed'
        const wrapped = new Error(errorMessage)
        this.maybeToastError(wrapped, 'Request failed', options)
        throw wrapped
      }
      const wrapped = new Error('Request failed')
      this.maybeToastError(error, 'Request failed', options)
      throw wrapped
    }
  }

  /**
   * Generic method for making authenticated PUT requests
   */
  async put<T>(
    endpoint: string,
    data?: unknown,
    headers?: Record<string, string>,
    options?: { suppressToast?: boolean; toastMessage?: string },
  ): Promise<T & { success: boolean }> {
    throwIfOfflineReadOnly(`api:put:${endpoint}`)

    try {
      const response = await api.put<T>(endpoint, convertToSnakeCase(data), { headers })
      const convertedData = convertToCamelCase(response.data)
      return {
        ...convertedData,
        success: response.status >= 200 && response.status < 300,
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        // For authentication errors (401/403), preserve the original axios error
        // so the sync service can detect the status code
        if (error.response.status === 401 || error.response.status === 403) {
          throw error
        }

        // For other errors, create a descriptive error message
        const errorData = error.response.data as { message?: string }
        const errorMessage = errorData.message || 'Request failed'
        const wrapped = new Error(errorMessage)
        this.maybeToastError(wrapped, 'Request failed', options)
        throw wrapped
      }
      const wrapped = new Error('Request failed')
      this.maybeToastError(error, 'Request failed', options)
      throw wrapped
    }
  }

  /**
   * Generic method for making authenticated PATCH requests
   */
  async patch<T>(
    endpoint: string,
    data?: unknown,
    headers?: Record<string, string>,
    options?: { suppressToast?: boolean; toastMessage?: string },
  ): Promise<T & { success: boolean }> {
    throwIfOfflineReadOnly(`api:patch:${endpoint}`)

    try {
      const response = await api.patch<T>(endpoint, convertToSnakeCase(data), { headers })
      const convertedData = convertToCamelCase(response.data)
      return {
        ...convertedData,
        success: response.status >= 200 && response.status < 300,
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        if (error.response.status === 401 || error.response.status === 403) {
          throw error
        }
        const errorData = error.response.data as { message?: string }
        const errorMessage = errorData.message || 'Request failed'
        const wrapped = new Error(errorMessage)
        this.maybeToastError(wrapped, 'Request failed', options)
        throw wrapped
      }
      const wrapped = new Error('Request failed')
      this.maybeToastError(error, 'Request failed', options)
      throw wrapped
    }
  }

  async getMaintenanceStatus(): Promise<MaintenanceStatusResponse> {
    const response = await this.get<MaintenanceStatusResponse>('/maintenance/', undefined, {
      suppressToast: true,
    })
    return { active: response.active }
  }

  /**
   * Generic method for downloading blobs
   */
  async getBlob(
    endpoint: string,
    headers?: Record<string, string>,
    options?: { suppressToast?: boolean; toastMessage?: string },
  ): Promise<Blob> {
    try {
      const response = await api.get(endpoint, {
        responseType: 'blob',
        headers,
      })
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const errorData = error.response.data as { message?: string }
        const errorMessage = errorData.message || 'Download failed'
        const wrapped = new Error(errorMessage)
        this.maybeToastError(wrapped, 'Download failed', options)
        throw wrapped
      }
      const wrapped = new Error('Download failed')
      this.maybeToastError(error, 'Download failed', options)
      throw wrapped
    }
  }

  /**
   * Generic method for uploading FormData
   */
  async postFormData<T>(
    endpoint: string,
    formData: FormData,
    options?: {
      suppressToast?: boolean
      toastMessage?: string
      /** Byte-level upload progress of the request body (loaded, total — total may be unknown). */
      onUploadProgress?: (loaded: number, total: number | undefined) => void
      signal?: AbortSignal
    },
  ): Promise<T & { success: boolean }> {
    throwIfOfflineReadOnly(`api:post-form-data:${endpoint}`)

    try {
      const response = await api.post<T>(endpoint, convertToSnakeCase(formData), {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: options?.onUploadProgress
          ? (event) => options.onUploadProgress?.(event.loaded, event.total)
          : undefined,
        ...(options?.signal ? { signal: options.signal } : {}),
      })
      const convertedData = convertToCamelCase(response.data)
      return {
        ...convertedData,
        success: response.status >= 200 && response.status < 300,
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        // For authentication errors (401/403), preserve the original axios error
        // so the sync service can detect the status code
        if (error.response.status === 401 || error.response.status === 403) {
          throw error
        }

        // For other errors, create a descriptive error message. Preserve the HTTP status so
        // callers (syncService) can classify validation rejections vs transient failures.
        const errorData = error.response.data as { message?: string; failed_events?: unknown }
        const errorMessage = errorData.message || 'Upload failed'
        const wrapped = new Error(errorMessage) as Error & {
          statusCode?: number
          failedEvents?: FailedEvent[]
        }
        wrapped.statusCode = error.response.status
        // Surface the server-named culprit(s) so callers identify rejected events by client_id
        // instead of parsing the message string (backend ask #1).
        if (Array.isArray(errorData.failed_events)) {
          wrapped.failedEvents = convertToCamelCase(errorData.failed_events) as FailedEvent[]
        }
        this.maybeToastError(wrapped, 'Upload failed', options)
        throw wrapped
      }
      const wrapped = new Error('Upload failed')
      this.maybeToastError(error, 'Upload failed', options)
      throw wrapped
    }
  }

  /**
   * Fetch workspaces for search with workspace_type=1
   */
  async getWorkspacesForSearch(): Promise<WorkspaceCatalogResponse> {
    const result = await this.get<WorkspaceCatalogResponse>(
      '/workspaces/?workspace_type=1',
      undefined,
      {
        suppressToast: true,
      },
    )
    if (result && typeof result === 'object') {
      return {
        workspaces: Array.isArray(result.workspaces) ? result.workspaces : [],
        personalTourCount:
          typeof result.personalTourCount === 'number' ? result.personalTourCount : 0,
      }
    }
    return {
      workspaces: [],
      personalTourCount: 0,
    }
  }

  /**
   * Fetch full workspace detail including permission fields.
   */
  async getWorkspaceDetail(clientId: string): Promise<WorkspaceDetailResponse> {
    const result = await this.get<WorkspaceDetailResponse>(
      `/workspaces/${clientId}`,
      undefined,
      { suppressToast: true }, // handled by syncService with dialog
    )
    return result
  }

  async getPinnedWorkspaceDetail(): Promise<WorkspaceDetailResponse> {
    return await this.get<WorkspaceDetailResponse>('/workspaces/pinned', undefined, {
      suppressToast: true,
    })
  }

  async bookmarkWorkspace(clientId: string): Promise<WorkspaceBookmarkResponse> {
    return await this.post<WorkspaceBookmarkResponse>(
      `/workspaces/${clientId}/bookmark`,
      undefined,
      undefined,
      { toastMessage: 'Failed to bookmark collection' },
    )
  }

  async unbookmarkWorkspace(clientId: string): Promise<WorkspaceBookmarkResponse> {
    return await this.delete<WorkspaceBookmarkResponse>(
      `/workspaces/${clientId}/bookmark`,
      undefined,
      { toastMessage: 'Failed to remove bookmark' },
    )
  }

  /**
   * Fetch fresh signed URLs for workspace photos without reloading full workspace detail.
   */
  async getWorkspacePhotoAssetUrls(clientId: string): Promise<WorkspacePhotoAssetUrlsResponse> {
    const result = await this.get<WorkspacePhotoAssetUrlsResponse>(
      `/workspaces/${clientId}/photo-asset-urls`,
      undefined,
      { suppressToast: true },
    )
    return result
  }

  async getUiSettings(): Promise<UiSettingsResponse> {
    try {
      const response = await api.get<RawUiSettingsResponse>('/accounts/ui-settings/')
      return normalizeUiSettingsResponse(response.data)
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        if (error.response.status === 401 || error.response.status === 403) {
          throw error
        }
        const errorData = error.response.data as { message?: string }
        const wrapped = new Error(errorData.message || 'Request failed')
        this.maybeToastError(wrapped, 'Failed to load UI settings', { suppressToast: true })
        throw wrapped
      }
      const wrapped = new Error('Request failed')
      this.maybeToastError(error, 'Failed to load UI settings', { suppressToast: true })
      throw wrapped
    }
  }

  async syncUiSettings(settings: UiSettingPayload[]): Promise<UiSettingsResponse> {
    throwIfOfflineReadOnly('api:patch:/accounts/ui-settings/')

    try {
      const response = await api.patch<RawUiSettingsResponse>('/accounts/ui-settings/', {
        settings,
      })
      return normalizeUiSettingsResponse(response.data)
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        if (error.response.status === 401 || error.response.status === 403) {
          throw error
        }
        const errorData = error.response.data as { message?: string }
        const wrapped = new Error(errorData.message || 'Request failed')
        this.maybeToastError(wrapped, 'Failed to sync UI settings', { suppressToast: true })
        throw wrapped
      }
      const wrapped = new Error('Request failed')
      this.maybeToastError(error, 'Failed to sync UI settings', { suppressToast: true })
      throw wrapped
    }
  }

  /**
   * Submit a batch of events to the server
   */
  async submitEventBatch(
    events: Event[],
    files: Record<string, File>,
    options?: {
      onUploadProgress?: (loaded: number, total: number | undefined) => void
      signal?: AbortSignal
    },
  ): Promise<BatchResponse & { success: boolean }> {
    const formData = new FormData()

    // Add each event as a string entry (converted to snake_case)
    events.forEach((event, index) => {
      const serverEvent = {
        clientId: event.clientId,
        type: event.type,
        timestamp: event.timestamp,
        payload: event.payload,
      }
      formData.append(`event[${index}]`, JSON.stringify(convertToSnakeCase(serverEvent)))
    })

    // Add each file with the appropriate key format
    Object.entries(files).forEach(([key, file]) => {
      const [eventIndex, fieldName] = key.split('_file_')
      formData.append(`event${eventIndex}_file_${fieldName}`, file)
    })

    return await this.postFormData<BatchResponse>('/events/batch', formData, {
      suppressToast: true, // syncService manages user messaging
      onUploadProgress: options?.onUploadProgress,
      signal: options?.signal,
    })
  }

  /**
   * Download a photo from the server
   */
  async downloadPhoto(url: string): Promise<Blob> {
    return await this.getBlob(url, undefined, { toastMessage: 'Failed to download photo' })
  }

  /**
   * Download a GPX file from the server
   */
  async downloadGpx(tourId: string, options?: { includeRoutePoints?: boolean }): Promise<Blob> {
    const query = new URLSearchParams()
    if (options?.includeRoutePoints) query.set('include_route_points', 'true')
    const qs = query.toString()
    const endpoint = `${this.buildTourEndpoint(tourId, 'gpx')}${qs ? `?${qs}` : ''}`
    return await this.getBlob(endpoint, undefined, {
      toastMessage: 'Failed to download GPX',
    })
  }

  async downloadWorkspaceGpx(
    workspaceId: string,
    options?: { includeRoutePoints?: boolean },
  ): Promise<Blob> {
    const query = new URLSearchParams()
    if (options?.includeRoutePoints) query.set('include_route_points', 'true')
    const qs = query.toString()
    const endpoint = `/workspaces/${workspaceId}/gpx${qs ? `?${qs}` : ''}`
    return await this.getBlob(endpoint, undefined, {
      toastMessage: 'Failed to download collection GPX',
    })
  }

  async downloadTrackGpx(
    trackId: string,
    options?: { includeRoutePoints?: boolean },
  ): Promise<Blob> {
    const query = new URLSearchParams()
    if (options?.includeRoutePoints) query.set('include_route_points', 'true')
    const qs = query.toString()
    const endpoint = `${this.buildTrackEndpoint(trackId, 'gpx')}${qs ? `?${qs}` : ''}`
    return await this.getBlob(endpoint, undefined, {
      toastMessage: 'Failed to download track GPX',
    })
  }

  // Workspace member management methods

  /**
   * Get all members of a workspace
   */
  async getWorkspaceMembers(
    clientId: string,
    options?: { suppressToast?: boolean; toastMessage?: string },
  ): Promise<WorkspaceMember[]> {
    const result = await this.get<WorkspaceMember[]>(
      `/workspaces/${clientId}/members`,
      undefined,
      options,
    )
    // Handle the case where the result is an object with numeric keys due to spreading an array
    if (result && typeof result === 'object' && 'success' in result) {
      const keys = Object.keys(result).filter((key) => key !== 'success' && !isNaN(Number(key)))
      return keys
        .map((key) => (result as unknown as Record<string, unknown>)[key] as WorkspaceMember)
        .filter(Boolean)
    }
    return []
  }

  /**
   * Add a member to a workspace
   */
  async addWorkspaceMember(
    clientId: string,
    userId: number,
    role: number,
    options?: { suppressToast?: boolean; toastMessage?: string },
  ): Promise<WorkspaceMember & { success: boolean }> {
    return await this.post<WorkspaceMember>(
      `/workspaces/${clientId}/members`,
      { userId, role },
      undefined,
      options,
    )
  }

  /**
   * Update a member's role in a workspace
   */
  async updateMemberRole(
    clientId: string,
    userId: number,
    role: number,
    options?: { suppressToast?: boolean; toastMessage?: string },
  ): Promise<WorkspaceMember & { success: boolean }> {
    try {
      const response = await this.put<WorkspaceMember>(
        `/workspaces/${clientId}/members/${userId}`,
        { role },
        undefined,
        options,
      )
      return response
    } catch (error) {
      throw new Error(
        `Failed to update member role: ${error instanceof Error ? error.message : String(error)}`,
      )
    }
  }

  /**
   * Remove a member from a workspace
   */
  async removeMember(
    clientId: string,
    userId: number,
    options?: { suppressToast?: boolean; toastMessage?: string },
  ): Promise<{ success: boolean }> {
    try {
      const response = await this.delete(
        `/workspaces/${clientId}/members/${userId}`,
        undefined,
        options,
      )
      return {
        success: response.success,
      }
    } catch (error) {
      throw new Error(
        `Failed to remove member: ${error instanceof Error ? error.message : String(error)}`,
      )
    }
  }

  /**
   * Invite a member to a workspace by email
   */
  async inviteMemberByEmail(
    clientId: string,
    email: string,
    role: number,
  ): Promise<{
    success: boolean
    message: string
    invitationSent: boolean
    userExists: boolean
  }> {
    return await this.post(`/workspaces/${clientId}/invite`, { email, role }, undefined, {
      suppressToast: true,
    })
  }

  /**
   * Get all pending invitations for a workspace
   */
  async getWorkspaceInvitations(
    clientId: string,
    options?: { suppressToast?: boolean; toastMessage?: string },
  ): Promise<WorkspaceInvitation[]> {
    const result = await this.get<WorkspaceInvitation[]>(
      `/workspaces/${clientId}/invitations`,
      undefined,
      options,
    )
    // Handle the case where the result is an object with numeric keys due to spreading an array
    if (result && typeof result === 'object' && 'success' in result) {
      const keys = Object.keys(result).filter((key) => key !== 'success' && !isNaN(Number(key)))
      return keys
        .map((key) => (result as unknown as Record<string, unknown>)[key] as WorkspaceInvitation)
        .filter(Boolean)
    }
    return []
  }

  /**
   * Get invited users (autocomplete source for member email)
   * Endpoint: /workspaces/me/invited-users
   */
  private invitedUsersCache: InvitedUser[] | null = null

  async getInvitedUsers(options?: {
    suppressToast?: boolean
    toastMessage?: string
    forceRefresh?: boolean
  }): Promise<InvitedUser[]> {
    if (this.invitedUsersCache && !options?.forceRefresh) {
      return this.invitedUsersCache
    }
    const result = await this.get<InvitedUser[]>('/workspaces/me/invited-users', undefined, {
      suppressToast: true,
      ...(options || {}),
    })
    let list: InvitedUser[] = []
    if (result && typeof result === 'object' && 'success' in result) {
      const keys = Object.keys(result).filter((key) => key !== 'success' && !isNaN(Number(key)))
      list = keys
        .map((key) => (result as unknown as Record<string, unknown>)[key] as InvitedUser)
        .filter(Boolean)
    } else if (Array.isArray(result)) {
      list = result
    }
    this.invitedUsersCache = list
    return list
  }

  /**
   * Cancel a pending invitation
   */
  async cancelInvitation(
    clientId: string,
    invitationId: number,
    options?: { suppressToast?: boolean; toastMessage?: string },
  ): Promise<{ success: boolean }> {
    try {
      const response = await this.delete(
        `/workspaces/${clientId}/invitations/${invitationId}`,
        undefined,
        options,
      )
      return {
        success: response.success,
      }
    } catch (error) {
      throw new Error(
        `Failed to cancel invitation: ${error instanceof Error ? error.message : String(error)}`,
      )
    }
  }

  /**
   * Get invitation information by token
   */
  async getInvitationInfo(token: string): Promise<{
    success: boolean
    inviter?: string
    workspaceName?: string
    userExists?: boolean
    error?: string
  }> {
    try {
      const response = await this.get<{
        inviter: string
        workspaceName: string
        userExists: boolean
      }>(`/workspaces/invitations/${token}`)
      return {
        success: true,
        inviter: response.inviter,
        workspaceName: response.workspaceName,
        userExists: response.userExists,
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get invitation info',
      }
    }
  }

  /**
   * Accept a workspace invitation
   */
  async acceptInvitation(
    token: string,
    profileData?: {
      profileName?: string
      password?: string
      acceptedTerms?: boolean
      termsVersion?: string
    },
  ): Promise<{
    success: boolean
    message: string
    userId?: number
    userEmail?: string
    userProfileName?: string
    workspaceClientId?: string
  }> {
    try {
      const response = await this.post<{
        message: string
        userId?: number
        userEmail?: string
        userProfileName?: string
        workspaceClientId?: string
      }>(`/workspaces/invitations/${token}/accept`, profileData || {}, undefined, {
        suppressToast: true, // handled by UI forms
      })
      return {
        success: response.success,
        message: response.message,
        userId: response?.userId,
        userEmail: response?.userEmail,
        userProfileName: response?.userProfileName,
        workspaceClientId: response.workspaceClientId,
      }
    } catch (error) {
      throw new Error(
        `Failed to accept invitation. ${error instanceof Error ? error.message : String(error)}`,
      )
    }
  }

  /**
   * Fetch a paginated, filtered list of tours system-wide
   */
  async getTours(
    params: {
      limit?: number
      name?: string
      noWorkspace?: boolean
      dateFrom?: string
      dateTo?: string
    } = {},
    options?: { suppressToast?: boolean; toastMessage?: string },
  ): Promise<ToursListResponse> {
    const query = new URLSearchParams()
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    if (params.name) query.set('name', params.name)
    if (params.noWorkspace) query.set('no_workspace', 'true')
    if (params.dateFrom) query.set('date_from', params.dateFrom)
    if (params.dateTo) query.set('date_to', params.dateTo)
    const qs = query.toString()
    const endpoint = `/tours/${qs ? `?${qs}` : ''}`
    const result = await this.get<ToursListResponse>(endpoint, undefined, {
      toastMessage: 'Failed to load tours',
      ...options,
    })

    // The generic get() spreads the response. When the API returns a plain array
    // the keys become numeric indices — detect and normalise to paginated shape.
    const numericKeys = Object.keys(result).filter((k) => k !== 'success' && !isNaN(Number(k)))
    if (numericKeys.length > 0) {
      const items = numericKeys.map(
        (k) => (result as unknown as Record<string, unknown>)[k] as (typeof result)['results'][0],
      )
      return { results: items, count: items.length, next: null, previous: null }
    }

    return result as unknown as ToursListResponse
  }

  async getFavoritePlaces(options?: {
    suppressToast?: boolean
    toastMessage?: string
  }): Promise<FavoritePlace[]> {
    return await this.getList<FavoritePlace>('/accounts/favorite-places/', undefined, {
      toastMessage: 'Failed to load favorite places',
      ...options,
    })
  }

  async createFavoritePlace(
    data: FavoritePlaceCreateRequest,
    options?: { suppressToast?: boolean; toastMessage?: string },
  ): Promise<FavoritePlace> {
    return await this.post<FavoritePlace>('/accounts/favorite-places/', data, undefined, {
      toastMessage: 'Failed to save favorite place',
      ...options,
    })
  }

  async deleteFavoritePlace(
    favoritePlaceId: number,
    options?: { suppressToast?: boolean; toastMessage?: string },
  ): Promise<{ message: string }> {
    return await this.delete<{ message: string }>(
      `/accounts/favorite-places/${favoritePlaceId}`,
      undefined,
      {
        toastMessage: 'Failed to remove favorite place',
        ...options,
      },
    )
  }

  async getInvitationQuotaStatus(options?: {
    suppressToast?: boolean
    toastMessage?: string
  }): Promise<InvitationQuotaStatus> {
    return await this.get<InvitationQuotaStatus>('/accounts/invitations/status/', undefined, {
      toastMessage: 'Failed to load invitation status',
      ...options,
    })
  }

  async getAccountInvitations(options?: {
    suppressToast?: boolean
    toastMessage?: string
  }): Promise<AccountInvitation[]> {
    return await this.getList<AccountInvitation>('/accounts/invitations/', undefined, {
      toastMessage: 'Failed to load invitations',
      ...options,
    })
  }

  async createAccountInvitation(
    data: { note?: string } = {},
    options?: {
      suppressToast?: boolean
      toastMessage?: string
    },
  ): Promise<AccountInvitationCreateResponse> {
    return await this.post<AccountInvitationCreateResponse>(
      '/accounts/invitations/',
      data,
      undefined,
      {
        toastMessage: 'Failed to create invitation',
        ...options,
      },
    )
  }

  async revokeAccountInvitation(
    invitationId: number,
    options?: { suppressToast?: boolean; toastMessage?: string },
  ): Promise<MessageResponse> {
    return await this.delete<MessageResponse>(`/accounts/invitations/${invitationId}`, undefined, {
      toastMessage: 'Failed to revoke invitation',
      ...options,
    })
  }

  async requestInviteAvailabilityNotification(options?: {
    suppressToast?: boolean
    toastMessage?: string
  }): Promise<MessageResponse> {
    return await this.post<MessageResponse>(
      '/accounts/invitations/notify-when-available/',
      {},
      undefined,
      {
        toastMessage: 'Failed to save notification request',
        ...options,
      },
    )
  }

  async getSmartSearchResults(
    params: {
      query?: string
      limit?: number
    } = {},
    options?: { suppressToast?: boolean; toastMessage?: string },
  ): Promise<SmartSearchAppResult[]> {
    const query = new URLSearchParams()
    if (params.query) query.set('query', params.query)
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    const qs = query.toString()
    const endpoint = `/search/smart/${qs ? `?${qs}` : ''}`
    const result = await this.get<SmartSearchResponse>(endpoint, undefined, {
      toastMessage: 'Failed to load Smart Search results',
      ...options,
    })
    return result.results ?? []
  }

  /**
   * Fetch full detail for a single tour by client ID.
   */
  async getTourDetail(
    clientId: string,
    options?: { suppressToast?: boolean; toastMessage?: string },
  ): Promise<Tour> {
    return await this.get<Tour>(`/tours/${clientId}`, undefined, {
      toastMessage: 'Failed to load tour details',
      ...options,
    })
  }

  /**
   * Mark a workspace for deletion
   */
  async deleteWorkspace(workspaceId: string): Promise<{ message: string }> {
    try {
      const response = await this.delete<{ message: string }>(
        `/workspaces/${workspaceId}`,
        undefined,
        { suppressToast: true }, // workspaceStore toasts on failure
      )
      return {
        message: response.message || 'Workspace marked for deletion',
      }
    } catch (error) {
      throw new Error(
        `Failed to delete workspace. ${error instanceof Error ? error.message : String(error)}`,
      )
    }
  }

  /**
   * Rename a workspace by ID
   */
  async renameWorkspace(workspaceId: string, name: string): Promise<{ message: string }> {
    try {
      const event: Event = {
        clientId: generateId(),
        type: EventType.WORKSPACE_UPDATED,
        timestamp: Date.now(),
        payload: {
          clientId: workspaceId,
          changes: [{ field: 'name', value: name }],
        },
      }
      await this.submitEventBatch([event], {})
      return { message: 'Workspace renamed successfully' }
    } catch (error) {
      throw new Error(
        `Failed to rename workspace. ${error instanceof Error ? error.message : String(error)}`,
      )
    }
  }

  /**
   * Restore a workspace that was marked for deletion
   */
  async restoreWorkspace(workspaceId: string): Promise<{ message: string }> {
    try {
      const response = await this.post<{ message: string }>(
        `/workspaces/${workspaceId}/restore`,
        {},
        undefined,
        { suppressToast: true }, // workspaceStore toasts on failure
      )
      return {
        message: response.message || 'Workspace restored successfully',
      }
    } catch (error) {
      throw new Error(
        `Failed to restore workspace. ${error instanceof Error ? error.message : String(error)}`,
      )
    }
  }

  /**
   * Leave a workspace the current user is a member of (self-removal).
   * Owners cannot leave — the backend returns 403 for them.
   */
  async leaveWorkspace(workspaceId: string): Promise<{ message: string }> {
    try {
      const response = await this.post<{ message: string }>(
        `/workspaces/${workspaceId}/leave`,
        {},
        undefined,
        { suppressToast: true }, // workspaceStore toasts on failure
      )
      return {
        message: response.message || 'You have left the collection',
      }
    } catch (error) {
      throw new Error(
        `Failed to leave workspace. ${error instanceof Error ? error.message : String(error)}`,
      )
    }
  }

  /**
   * Update workspace visibility (public/private status)
   */
  async updateWorkspaceVisibility(
    workspaceId: string,
    isPublic: boolean,
  ): Promise<{ success: boolean; message: string; isPublic: boolean }> {
    try {
      const response = await this.put<{
        success: boolean
        message: string
        isPublic: boolean
      }>(`/workspaces/${workspaceId}/visibility`, { is_public: isPublic }, undefined, {
        suppressToast: true, // workspaceStore handles error toast
      })
      return response
    } catch (error) {
      throw new Error(
        `Failed to update workspace visibility. ${error instanceof Error ? error.message : String(error)}`,
      )
    }
  }

  /**
   * Bulk-fetch track geometry at a specific resolution for workspaces.
   */
  async getWorkspaceTracksGeometry(
    workspaceClientIds: string[],
    resolution: '50m' | '5m',
    signal?: AbortSignal,
  ): Promise<Array<{ clientId: string; tracksGeometry: Array<string | null> }>> {
    const response = await api.post(
      '/workspaces/tracks-geometry',
      convertToSnakeCase({ workspaceClientIds, resolution }),
      { signal },
    )
    return convertToCamelCase(response.data) as Array<{
      clientId: string
      tracksGeometry: Array<string | null>
    }>
  }

  /**
   * Bulk-fetch track geometry at a specific resolution for tours.
   */
  async getTourTracksGeometry(
    tourClientIds: string[],
    resolution: '50m' | '5m',
    signal?: AbortSignal,
  ): Promise<Array<{ clientId: string; tracksGeometry: Array<string | null> }>> {
    const response = await api.post(
      '/tours/tracks-geometry',
      convertToSnakeCase({ tourClientIds, resolution }),
      { signal },
    )
    return convertToCamelCase(response.data) as Array<{
      clientId: string
      tracksGeometry: Array<string | null>
    }>
  }
}

// Export as singleton
export const apiService = new APIService()
